import { useState, useEffect } from "react";
import {
  ActionTypeEnum,
  AcTransactionModel,
  AcTransactionTypeEnum,
  LookupItemModel,
  LookupTypeEnum,
  RequestActionModel,
  ResponseBaseModel,
  ToastModel,
  ValidationErrorModel,
} from "../../../models";
import { getLookupByType } from "../../../serviceBroker/lookupApiServiceBroker";
import { getUserId, scrollToDocumentTop } from "../../../utils";

import { selectAcTransaction } from "../../../serviceBroker/acTransactionsApiServiceBroker";
import {
  getAccountBalance,
  getFromAccountIdBasedOnTransactionType,
  getToAccountIdBasedOnTransactionType,
} from "../../../components/acTransaction/businessLogic/acTransactionBl";
import { useNavigate, useSearchParams } from "react-router-dom";


export const useAcTransaction = (acTransactionType: AcTransactionTypeEnum) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  //#region  variables
  const acTransactionInitValue: AcTransactionModel = {
    ID: 0,
    FromAccount_ID: getFromAccountIdBasedOnTransactionType(acTransactionType),
    ToAccount_ID: getToAccountIdBasedOnTransactionType(acTransactionType),
    CostCenter_ID: 0,
    Currency_ID: 0,
    Station_ID: 1,
    Employee_Id: 0,
    Code: "",
    Note: "",
    Value: 0,
    Date: new Date(),
    AcTransactionType: acTransactionType,
    CreatedBy: getUserId(),
    ModifiedBy: 0,
    CreationDate: new Date(),
    ModificationDate: new Date(),
    rowState: 1,
    ValueCurrency: 1,
    isNewRequest: true,
    balance: 0,
    fromAccountBalance: 0,
    toAccountBalance: 0,
  };
  //#endregion
  //#region states
  const [loading, setLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [toastModel, setToastModel] = useState<ToastModel>({ show: false });
  const [showDeleteModel, setShowDeleteModel] = useState(false);
  const [showPrintModel, setShowPrintModel] = useState({
    show: false,
    content: "",
  });
  const [acTransaction, setAcTransaction] = useState<AcTransactionModel>(
    acTransactionInitValue
  );
  const [validationErrors, setValidationErrors] = useState<
    ValidationErrorModel[]
  >([]);
  const [accountLookups, setAccountLookups] = useState<LookupItemModel[]>([]);
  const [inOutAccountForPaymentLookups, setInOutAccountForPaymentLookups] =
    useState<LookupItemModel[]>([]);
  const [currencyLookUps, setCurrencyLookUps] = useState<LookupItemModel[]>([]);
  const [coastCenterLookUps, setCoastCenterLookUps] = useState<
    LookupItemModel[]
  >([]);
  const [userLookUps, setUserLookUps] = useState<LookupItemModel[]>([]);
  const [employeeLookUps, setEmloyeeLookUps] = useState<LookupItemModel[]>([]);
  const [showPrintoutPdfModel, setShowPrintoutPdfModel] = useState(false);
  const [printTransactionId, setPrintTransactionId] = useState(0);

  const [fetchLookupCompleted, setFetchLookupCompleted] = useState(false);
  //#endregion
  //#region functions
  const urlHasParameters = (): boolean => searchParams.toString() !== "";
  const reloadWithoutParams = () => {
    navigate(location.pathname, { replace: true });
  };
  const getAllLookups = async () => {
    let lookupAccountTransactionType: LookupTypeEnum = LookupTypeEnum.None;
    switch (+acTransactionType) {
      case AcTransactionTypeEnum.MoneyOut:
        lookupAccountTransactionType = LookupTypeEnum.AllTreasuryOutAccount;
        break;
      case AcTransactionTypeEnum.MoneyIn:
        lookupAccountTransactionType = LookupTypeEnum.AllTreasuryInAccount;
        break;
      case AcTransactionTypeEnum.SupplierPayments:
        lookupAccountTransactionType = LookupTypeEnum.SupplierAccounts;
        break;
      case AcTransactionTypeEnum.RefundSupplierPayments:
        lookupAccountTransactionType = LookupTypeEnum.SupplierRefundAccounts;
        break;
      case AcTransactionTypeEnum.CustomerPayment:
        lookupAccountTransactionType = LookupTypeEnum.CustomerAccounts;
        break;
      case AcTransactionTypeEnum.RefundCustomerPayments:
        lookupAccountTransactionType = LookupTypeEnum.CustomerRefundAccounts;
        break;
    }

    const [
      users,
      currencies,
      coastCenters,
      accounts,
      inOutAccounts,
      employees,
    ] = await Promise.all([
      getLookupByType(LookupTypeEnum.Users, true, false),
      getLookupByType(LookupTypeEnum.Currency, true, false),
      getLookupByType(LookupTypeEnum.CostCenters, true, false),
      getLookupByType(lookupAccountTransactionType, false, false),
      getLookupByType(LookupTypeEnum.InOutAccountsForPayments, false, false),
      getLookupByType(LookupTypeEnum.Employee, true, false),
    ]);

    setAccountLookups(accounts);
    setUserLookUps(users);
    setCurrencyLookUps(currencies);
    setCoastCenterLookUps(coastCenters);
    setInOutAccountForPaymentLookups(inOutAccounts);
    setEmloyeeLookUps(employees);
  };
  const handleModifyRequest = async (
    request: ResponseBaseModel<AcTransactionModel>
  ): Promise<ResponseBaseModel<AcTransactionModel>> => {
    if (
      request !== null &&
      request !== undefined &&
      request.Result !== null &&
      request.Result !== undefined
    ) {
      request.Result.balance = !request.Result.isNewRequest
        ? await getAccountBalance(
            request.Result.FromAccount_ID ?? 0,
            request.Result.Currency_ID ?? 0
          )
        : request.Result.balance;
      request.Result.isNewRequest = false;
      //request.Result.Value =request.Result.Value * request.Result.ValueCurrency;
    }
    return request;
  };
  const handleAction = async (request: RequestActionModel) => {
    switch (request.action) {
      case ActionTypeEnum.AddSuccess:
      case ActionTypeEnum.DeleteSuccess:
      case ActionTypeEnum.SaveSuccess:
      case ActionTypeEnum.Success:
        if (urlHasParameters()) {
          reloadWithoutParams();
        }
        scrollToDocumentTop();
        setValidationErrors([]);
        setToastModel({ ...toastModel, show: true });
        setAcTransaction(acTransactionInitValue);
        setShowDeleteModel(false);
        setIsRefresh(true);
        break;
      case ActionTypeEnum.Add:
        setAcTransaction(acTransactionInitValue);
        break;
      case ActionTypeEnum.DeleteStart:
        setAcTransaction(request.request);
        setShowDeleteModel(true);
        break;
      case ActionTypeEnum.DeleteFailed:
        setValidationErrors(request.request);
        setShowDeleteModel(false);
        scrollToDocumentTop();
        break;
      case ActionTypeEnum.DeleteReset:
        setValidationErrors([]);
        setAcTransaction(acTransactionInitValue);
        setShowDeleteModel(false);
        break;
      case ActionTypeEnum.ClearError:
        setValidationErrors([]);
        break;
      case ActionTypeEnum.Clear:
        scrollToDocumentTop();
        setAcTransaction(acTransactionInitValue);
        setValidationErrors([]);
        break;
      case ActionTypeEnum.RaiseError:
        scrollToDocumentTop();
        setValidationErrors(request.request);
        break;
      case ActionTypeEnum.Failed:
        setValidationErrors(request.request.Errors);
        break;
      case ActionTypeEnum.Update:
        setLoading(true);

        scrollToDocumentTop();

        let response = await selectAcTransaction(request.id!);
        response = await handleModifyRequest(response);
        if (response?.Result != null) {
          response.Result.fromAccountBalance =
            (await getAccountBalance(
              response?.Result?.FromAccount_ID ?? 0,
              response?.Result?.Currency_ID ?? 0
            )) ?? 0;
          response.Result.toAccountBalance =
            (await getAccountBalance(
              response?.Result?.ToAccount_ID ?? 0,
              response?.Result?.Currency_ID ?? 0
            )) ?? 0;
        }
        setAcTransaction(response.Result || acTransactionInitValue);
        setLoading(false);
        break;
      case ActionTypeEnum.Delete:
        setLoading(true);
        setAcTransaction(request.request);
        setShowDeleteModel(true);
        setLoading(false);
        break;
      case ActionTypeEnum.PrintPdf:
        setPrintTransactionId(request.id || 0);
        setShowPrintoutPdfModel(true);
        break;
    }
  };
  //#endregion
  //#region useEffects
  useEffect(() => {
    const fillData = async () => {
      setLoading(true);
      setFetchLookupCompleted(false);
      await getAllLookups();
      setFetchLookupCompleted(true);
      setLoading(false);
    };
    fillData().then(() => {});
  }, []);
  //#endregion
  //#region return
  return {
    // States
    loading,
    setLoading,
    isRefresh,
    setIsRefresh,
    toastModel,
    setToastModel,
    showDeleteModel,
    setShowDeleteModel,
    showPrintModel,
    setShowPrintModel,
    acTransaction,
    setAcTransaction,
    validationErrors,
    setValidationErrors,
    accountLookups,
    inOutAccountForPaymentLookups,
    currencyLookUps,
    coastCenterLookUps,
    userLookUps,
    employeeLookUps,
    showPrintoutPdfModel,
    setShowPrintoutPdfModel,
    printTransactionId,
    setPrintTransactionId,
    fetchLookupCompleted,
    // Methods
    handleAction,
    handleModifyRequest,
    // Constants
    acTransactionInitValue,
  };
  //#endregion
};
